import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CommonService } from './common.service';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class InactivityLogoutService {
  // Configuration
  private readonly IDLE_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
  private readonly WARNING_TIME = 5 * 60 * 1000; // 5 minutes before logout (warning shown)
  
  // Timers
  private idleTimer: any = null;
  private warningTimer: any = null;
  private countdownInterval: any = null;
  
  // State
  private isIdle = false;
  private isWarningShown = false;
  
  // Event handlers storage for cleanup
  private activityHandlers: Map<string, () => void> = new Map();
  
  // Events
  public onWarning = new Subject<number>(); // Emits seconds remaining
  public onLogout = new Subject<void>();
  
  constructor(
    private router: Router,
    private commonService: CommonService,
    private ngZone: NgZone
  ) {
    // Initialize tracking only if user is already logged in
    // Otherwise, it will be initialized after login
    if (this.commonService.isLoggedIn()) {
      this.initActivityTracking();
    }
  }
  
  /**
   * Initialize activity tracking
   */
  private initActivityTracking(): void {
    // Only track if user is logged in
    if (!this.commonService.isLoggedIn()) {
      return;
    }
    
    // Remove existing listeners first to avoid duplicates
    this.removeActivityListeners();
    
    // Track various user activities
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      const handler = () => this.resetTimer();
      document.addEventListener(event, handler, true);
      // Store handler for later removal
      this.activityHandlers.set(event, handler);
    });
    
    // Start the timer
    this.startTimer();
  }
  
  /**
   * Remove activity listeners
   */
  private removeActivityListeners(): void {
    this.activityHandlers.forEach((handler, event) => {
      document.removeEventListener(event, handler, true);
    });
    this.activityHandlers.clear();
  }
  
  /**
   * Start the inactivity timer
   */
  private startTimer(): void {
    this.ngZone.runOutsideAngular(() => {
      // Clear existing timers
      this.clearTimers();
      
      // Set warning timer (IDLE_TIMEOUT - WARNING_TIME)
      const warningDelay = this.IDLE_TIMEOUT - this.WARNING_TIME;
      
      this.warningTimer = setTimeout(() => {
        this.ngZone.run(() => {
          this.showWarning();
        });
      }, warningDelay);
      
      // Set logout timer
      this.idleTimer = setTimeout(() => {
        this.ngZone.run(() => {
          this.logout();
        });
      }, this.IDLE_TIMEOUT);
    });
  }
  
  /**
   * Reset the inactivity timer
   */
  public resetTimer(): void {
    if (!this.commonService.isLoggedIn()) {
      return;
    }
    
    // If warning was shown, hide it
    if (this.isWarningShown) {
      this.hideWarning();
    }
    
    // Restart the timer
    this.startTimer();
  }
  
  /**
   * Show warning before logout
   */
  private showWarning(): void {
    if (this.isWarningShown) {
      return;
    }
    
    this.isWarningShown = true;
    this.isIdle = true;
    
    // Emit warning event with seconds remaining
    const secondsRemaining = Math.floor(this.WARNING_TIME / 1000);
    this.onWarning.next(secondsRemaining);
    
    // Start countdown
    this.startCountdown();
  }
  
  /**
   * Start countdown timer
   */
  private startCountdown(): void {
    // Clear any existing countdown
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    
    let secondsRemaining = Math.floor(this.WARNING_TIME / 1000);
    
    this.countdownInterval = setInterval(() => {
      secondsRemaining--;
      this.onWarning.next(secondsRemaining);
      
      if (secondsRemaining <= 0) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
        // Trigger logout immediately when countdown reaches 0
        this.ngZone.run(() => {
          this.logout();
        });
      }
    }, 1000);
  }
  
  /**
   * Hide warning
   */
  private hideWarning(): void {
    this.isWarningShown = false;
    this.isIdle = false;
    this.onWarning.next(0); // Signal to hide warning
  }
  
  /**
   * Logout user due to inactivity
   */
  private logout(): void {
    // Prevent multiple logout calls
    if (!this.commonService.isLoggedIn()) {
      return;
    }
    
    // Clear all timers first
    this.clearTimers();
    
    // Remove activity listeners
    this.removeActivityListeners();
    
    // Reset state
    this.isWarningShown = false;
    this.isIdle = false;
    
    // Emit logout event
    this.onLogout.next();
    
    // Perform logout (this will clear cookies and navigate)
    this.commonService.logout();
  }
  
  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }
  
  /**
   * Stop inactivity tracking (e.g., on manual logout)
   */
  public stopTracking(): void {
    this.clearTimers();
    this.removeActivityListeners();
    this.isWarningShown = false;
    this.isIdle = false;
  }
  
  /**
   * Resume inactivity tracking (e.g., after login)
   */
  public resumeTracking(): void {
    if (this.commonService.isLoggedIn()) {
      this.initActivityTracking();
    }
  }
  
  /**
   * Get remaining idle time in seconds
   */
  public getRemainingTime(): number {
    return Math.floor(this.WARNING_TIME / 1000);
  }
}

